/* istanbul ignore file */
import React, { useContext, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { noop, map, mapObject, includes } from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  Button,
} from '@fluentui/react-components';
import { BundleCouponClassCodes } from '@bingads-webui-campaign-react/promotion-utils';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { extractSourceId } from '@bingads-webui-campaign-react/pmax-landing-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import CoinAndDollarImage from '../../media/coupon.png';
import { CouponTemplateMapping } from './coupon-template';
import { getCouponBannerStyles } from '../styles/coupon-banner-styles';

export const CouponBanner = ({ onClickClaimCoupon, logger }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getCouponBannerStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const serverContext = useContext(ServerContext);

  const {
    Urls: {
      PMaxLiteSignup: signUpUrl,
    } = {},
    AssignCouponClassId = '',
  } = serverContext;

  const isBundleCoupon = includes(BundleCouponClassCodes, AssignCouponClassId);
  const sourceId = isBundleCoupon ? extractSourceId() : extractSourceId(signUpUrl);
  const couponTemplate = sourceId ? CouponTemplateMapping(isBundleCoupon).find(info => (
    map(info.sourceIdList, item => item.toLowerCase()).includes(sourceId.toLowerCase())
  )) : null;

  const onOpenChange = useCallback((_, data) => {
    if (data.open) {
      logger.trace('Coupon details dialog opened');
    }
  }, [logger]);

  if (!couponTemplate) {
    return null;
  }

  const { template, values } = couponTemplate;
  const translatedValues = mapObject(values, v => i18n.getString(v));
  const bannerString = i18n.getString(template.banner, translatedValues);
  const headerString = i18n.getString(template.header, translatedValues);
  const contentString = `${i18n.getString(template.contentMarket, translatedValues)} ${i18n.getString(template.contentRedeem, translatedValues)}  ${i18n.getString(template.contentPayment)}`;

  return (
    <div className={classNames('coupon-banner', classes.container, classes[`container-${displayMode}`])}>
      <div className={classNames('coupon-overlay-background', classes.couponOverlayBase, classes.couponOverlayBackground)} />
      <div className={classNames('coupon-overlay-content', classes.couponOverlayBase, classes.couponOverlayContent)} >
        <div className={classNames('coin-image-container', classes.coinImageContainer, classes[`coinImageContainer-${displayMode}`])}>
          <img src={CoinAndDollarImage} alt={i18n.getString(_TL_('Coupon Image'))} />
        </div>
        <div className={classNames('coupon-text', classes.couponText, classes[`couponText-${displayMode}`])}>
          {bannerString}{' '}
          <Dialog modalType="non-modal" onOpenChange={onOpenChange}>
            <DialogTrigger disableButtonEnhancement>
              <span
                role="button"
                className={classNames('coupon-details', classes.couponDetails)}
                tabIndex={0}
              >
                {i18n.getString(_TL_('Learn more'))}
              </span>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{headerString}</DialogTitle>
                <DialogContent>
                  <div className={classNames('coupon-details-content', classes.couponDetailsContent)}>
                    {onClickClaimCoupon &&
                      <Button
                        className={classNames('claim-coupon-button', classes.claimCouponBtn)}
                        onClick={onClickClaimCoupon}
                        appearance="primary"
                      >
                        {i18n.getString(_TL_('Claim coupon'))}
                      </Button>
                    }
                    <div className="coupon-content">{contentString}</div>
                  </div>
                </DialogContent>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

CouponBanner.propTypes = {
  onClickClaimCoupon: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

CouponBanner.defaultProps = {
  logger: {
    trace: noop,
  },
};
